"use client";

import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { PortalIcon } from '@/components/PortalIcon';
import { useConsent } from '@/contexts/ConsentContext';
import { useTranslations } from 'next-intl';


// Combined optional-consent screen, fired by ConsentGate after Bucket C is in
// place. Asks Bucket A (anonymous telemetry) and Bucket B (AI research corpus)
// together in one dialog. Defaults OFF for both.
//
// Renders only while the user has no row at all for A or B. Once both are
// recorded — granted or not — this screen never reappears. Settings → Privacy
// is the only re-entry point afterwards.
export const PostVerifyConsentScreen: React.FC = () => {
  const t = useTranslations('welcomeConsent');
  const { setConsent } = useConsent();
  const [a, setA] = useState(false);
  const [b, setB] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (grantedA: boolean, grantedB: boolean) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      // Both rows must land together. Promise.all narrows the partial-write
      // window: one INSERT failing rejects the whole call, leaving the user
      // on the screen instead of half-decided. Optimistic state update in
      // setConsent is sufficient — no extra refresh() needed and re-fetching
      // here risks reverting state if a read replica is lagging.
      await Promise.all([
        setConsent('A', grantedA),
        setConsent('B', grantedB),
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true}>
      <DialogContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        showCloseButton={false}
        className="max-w-lg p-0 overflow-hidden"
      >
        <div className="relative px-8 pt-10 pb-6 text-center">
          <div className="relative mx-auto w-fit">
            <div className="absolute inset-0 -m-4 rounded-full bg-pacific-500/20 blur-2xl" aria-hidden />
            <div className="relative rounded-full bg-graphite-900/70 p-4 ring-1 ring-pacific-500/20 backdrop-blur-xs">
              <PortalIcon size={40} />
            </div>
          </div>

          <DialogTitle className="mt-6 text-2xl font-bold tracking-tight text-fg">
            {t('title')}
          </DialogTitle>

          <p className="mt-3 text-sm leading-relaxed text-fg-secondary max-w-md mx-auto">
            {t('intro')}
          </p>
        </div>

        <div className="px-8 pb-2 space-y-3">
          <Toggle
            checked={a}
            onChange={setA}
            label={t('telemetry.label')}
            help={t('telemetry.help')}
          />
          <Toggle
            checked={b}
            onChange={setB}
            label={t('research.label')}
            help={t('research.help')}
          />
        </div>

        <div className="border-t border-border bg-graphite-950/40 px-8 py-5 space-y-4">
          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-400">
              {error}
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => save(false, false)}
              disabled={busy}
            >
              {t('skip')}
            </Button>
            <Button
              variant="cta"
              size="sm"
              onClick={() => save(a, b)}
              disabled={busy}
            >
              {busy ? t('saving') : t('save')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface ToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label: string;
  help: string;
}

const Toggle: React.FC<ToggleProps> = ({ checked, onChange, label, help }) => (
  <label className="flex items-start gap-3 cursor-pointer rounded-xl border border-border bg-graphite-950/30 px-4 py-3 hover:bg-surface-hover transition-colors">
    <span className="relative mt-0.5 flex-shrink-0 w-4 h-4 inline-flex items-center justify-center">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="peer absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
      />
      <span className="absolute inset-0 rounded-sm border border-border bg-surface-hover peer-hover:border-border-strong peer-checked:bg-fg peer-checked:border-fg peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-1 transition-colors" />
      <Check
        strokeWidth={3}
        className="absolute w-3 h-3 text-canvas opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none"
      />
    </span>
    <span className="flex-1">
      <span className="block text-sm font-medium text-fg leading-snug">{label}</span>
      <span className="mt-1 block text-xs text-fg-secondary leading-relaxed">{help}</span>
    </span>
  </label>
);
